// core/interceptors/auth.interceptor.ts
import {
  HttpContextToken,
  HttpResponse,
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from "@angular/common/http";
import { inject } from "@angular/core";
import { Observable, from, switchMap, throwError, tap } from "rxjs";
import { catchError } from "rxjs/operators";
import { AuthService } from "../services/auth.service";
import { AppConfigService } from "../services";
import { HttpErrorsService } from "../services/http-errors.service";
import { AuthStateService } from "../services/auth-state.service";

/** Evita bucles: máximo un ciclo refresh+retry por request original. */
const AUTH_REFRESH_HANDLED = new HttpContextToken<boolean>(() => false);

export const authInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);
  const authState = inject(AuthStateService);
  const appConfigService = inject(AppConfigService);
  const httpErrors = inject(HttpErrorsService);

  let isFromBackendAPI = false;
  try {
    const apiUrl = appConfigService.get("apiUrl");
    isFromBackendAPI = request.url.includes(apiUrl);

  } catch {
    // Config no inicializada aún; seguimos sin token
  }

  const isLogin = request.url.includes("/login");
  const isRefresh = request.url.includes("/refresh-token");
  const isLogout = request.url.includes("/logout");
  const isAuthEndpoint = isLogin || isRefresh || isLogout;
  const shouldAttachBearer = isFromBackendAPI && (!isAuthEndpoint || isLogout);
  // Login y refresh actualizan el estado de forma atómica en AuthService.
  // No sincronizar aquí evita que una respuesta tardía restaure solo el token
  // después de que la sesión ya fue invalidada.
  const shouldSyncTokenFromResponse = isFromBackendAPI && !isAuthEndpoint;

  if (shouldAttachBearer) {
    const token = authService.getToken();
    if (token && !authState.isGuest()) request = addToken(request, token);
  }

  if (isFromBackendAPI && isAuthEndpoint) {
    request = request.clone({ withCredentials: true });
  }

  const syncToken = (event: HttpEvent<unknown>): void => {
    if (!shouldSyncTokenFromResponse || !(event instanceof HttpResponse)) {
      return;
    }

    const responseToken = (event.body as { token?: string } | null)?.token;
    if (event.ok && responseToken) {
      authService.setToken(responseToken);
    }
  };

  const send = (req: HttpRequest<unknown>): Observable<HttpEvent<unknown>> =>
    next(req).pipe(tap({ next: syncToken }));

  return send(request).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401 || !isFromBackendAPI || isAuthEndpoint) {
        return throwError(() => error);
      }

      if (authState.isLoggingOut() || authState.isGuest()) {
        return throwError(() => error);
      }

      // Este request ya pasó por refresh+retry y volvió a fallar → sesión inválida.
      if (request.context.get(AUTH_REFRESH_HANDLED)) {
        httpErrors.handleError(401);
        return throwError(() => error);
      }

      // Renovar en silencio usando la cookie HttpOnly de la sesión.
      if (!authService.shouldAttemptTokenRefresh()) {
        httpErrors.handleError(401, undefined, undefined, {
          redirectToGoogle: true,
        });
        return throwError(() => error);
      }

      const tokenBeforeRefresh = authService.getToken();

      return from(authService.refreshToken()).pipe(
        switchMap((refreshResponse) => {
          const newToken = refreshResponse.body?.token;

          if (!refreshResponse.body?.isSuccess || !newToken) {
            httpErrors.handleError(401);
            return throwError(() => error);
          }

          return send(markRefreshHandled(addToken(request, newToken))).pipe(
            catchError((retryError: HttpErrorResponse) =>
              recoverAfterRetry401(
                request,
                retryError,
                newToken,
                authService,
                httpErrors,
                send,
              ),
            ),
          );
        }),
        catchError((refreshError: HttpErrorResponse) => {
          const tokenAfterRefresh = authService.getToken();
          if (
            tokenAfterRefresh &&
            tokenAfterRefresh !== tokenBeforeRefresh
          ) {
            return send(
              markRefreshHandled(addToken(request, tokenAfterRefresh)),
            ).pipe(
              catchError((retryError: HttpErrorResponse) => {
                if (retryError.status === 401) {
                  httpErrors.handleError(401);
                }
                return throwError(() => retryError);
              }),
            );
          }

          if (refreshError.status === 401) {
            httpErrors.handleError(401);
          }

          return throwError(() => refreshError);
        }),
      );
    }),
  );
};

function markRefreshHandled(
  request: HttpRequest<unknown>,
): HttpRequest<unknown> {
  return request.clone({
    context: request.context.set(AUTH_REFRESH_HANDLED, true),
  });
}

function addToken(
  request: HttpRequest<unknown>,
  token: string,
): HttpRequest<unknown> {
  return request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}

function recoverAfterRetry401(
  request: HttpRequest<unknown>,
  retryError: HttpErrorResponse,
  tokenUsedOnRetry: string,
  authService: AuthService,
  httpErrors: HttpErrorsService,
  send: (req: HttpRequest<unknown>) => Observable<HttpEvent<unknown>>,
): Observable<HttpEvent<unknown>> {
  if (retryError.status !== 401) {
    return throwError(() => retryError);
  }

  const retryWithToken = (
    token: string,
  ): Observable<HttpEvent<unknown>> =>
    send(markRefreshHandled(addToken(request, token))).pipe(
      catchError((secondError: HttpErrorResponse) => {
        if (secondError.status === 401) {
          httpErrors.handleError(401);
        }
        return throwError(() => secondError);
      }),
    );

  // Carrera con UpdateToken / otra petición: el access en memoria ya rotó.
  const latestToken = authService.getToken();
  if (latestToken && latestToken !== tokenUsedOnRetry) {
    return retryWithToken(latestToken);
  }

  // Misma carrera si el tap del otro request aún no actualizó memoria:
  // un refresh extra (dedupe) recupera sesión sin mostrar "expirada".
  return from(authService.refreshToken()).pipe(
    switchMap((refreshResponse) => {
      const token = refreshResponse.body?.token;
      if (!refreshResponse.body?.isSuccess || !token) {
        httpErrors.handleError(401);
        return throwError(() => retryError);
      }
      return retryWithToken(token);
    }),
    catchError((refreshError: HttpErrorResponse) => {
      if (refreshError.status === 401) {
        httpErrors.handleError(401);
      }
      return throwError(() => refreshError);
    }),
  );
}
